/** Search relevance ranking — used across pages with a free-text filter
 *  (Spreads, Orderbooks, TrackerState, DexMutes, TaxCache, Mutes) so a
 *  query for ``eth`` surfaces the ETH entity BEFORE ETHFI / WETH /
 *  ETH-USDT-pair noise.
 *
 *  Ranks (lower = better):
 *    0 — primary equals query                      (eth → eth)
 *    1 — primary starts with query                 (eth → ethfi)
 *    2 — primary contains query (substring)        (eth → kreth)
 *    3 — match only in secondary haystack          (exchange / symbol /
 *        network — keeps the row visible without promoting it)
 *    null — no match anywhere → row excluded
 *
 *  ``primary`` is the user-facing identifier (entity / asset / symbol).
 *  Leading sigils (``$BTC``, ``btc:2``, ``_USDT``) are stripped so
 *  searches for ``btc`` still hit prefix-rank — same behaviour as the
 *  Orderbooks pipeline before this helper was extracted.
 *
 *  Empty query returns rank 2 unconditionally so callers can use the
 *  rank as a stable sort key without branching on ``q``. */

export type SearchRank = 0 | 1 | 2 | 3;

const SIGIL_RE = /^[$_:]+/;

export function searchRank(
  query: string,
  primary: string,
  secondary?: string,
): SearchRank | null {
  if (!query) return 2;
  const q = query.toLowerCase();
  const p = primary.toLowerCase();
  const pClean = p.replace(SIGIL_RE, "");
  if (p === q || pClean === q) return 0;
  if (p.startsWith(q) || pClean.startsWith(q)) return 1;
  if (p.includes(q)) return 2;
  if (secondary && secondary.toLowerCase().includes(q)) return 3;
  return null;
}

/** Convenience: filter+sort an array by search relevance, preserving
 *  the caller's original order within each rank bucket (stable). The
 *  caller can pre-sort by their own metric (profit, age, ...) and rely
 *  on this to apply the rank-based ordering on top.
 *
 *  ``getPrimary`` and ``getSecondary`` extract the haystacks per item.
 *  Items returning ``null`` (no match) are excluded.
 *
 *  When ``query`` is empty the input is returned untouched (no
 *  allocations, original order preserved). */
export function filterByRank<T>(
  items: readonly T[],
  query: string,
  getPrimary: (item: T) => string,
  getSecondary?: (item: T) => string,
): T[] {
  if (!query) return [...items];
  const ranked: { item: T; rank: SearchRank; idx: number }[] = [];
  items.forEach((item, idx) => {
    const r = searchRank(
      query,
      getPrimary(item),
      getSecondary ? getSecondary(item) : undefined,
    );
    if (r != null) ranked.push({ item, rank: r, idx });
  });
  ranked.sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx));
  return ranked.map((r) => r.item);
}
